import { accountRelationshipsKeys, accountRelationshipsQueries, accountRelationshipsService, accountsKeys, accountsQueries, accountsService, queryClient, toApiError, usersKeys, usersQueries, usersService } from '@/api'
import { appConfig } from '@/config/app-config'
import { fail, ok, type Outcome } from '@/store/commit'
import type { AppThunk } from '@/store/create-store'

import { relationshipTypeOf, rolesFor, toAccount, toDirectory, toUser } from './mappers'
import { normalizeEmail, normalizeName } from './normalize'
import { canCreateAccountType, canManageUsers, isSupplyLogicAdmin, usersScopeOf } from './permissions'
import { accountNameSchema, addressSchema, firstIssue, personSchema } from './schemas'
import { findUserByEmail, selectAccountById, selectAllAccounts, selectAllUsers, selectCurrentUser, selectUserById } from './selectors'
import { directoryLoaded } from './store'
import { hasAddress, ROLES_BY_ACCOUNT_TYPE, type Account, type AccountType, type Address, type Role, type User } from './types'

export interface PersonInput {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
}

/**
 * What the Create User form can ask for. The account is NEVER taken from a free-form field for supplier and client
 * admins: `OWN_ACCOUNT` resolves it from the signed-in user, so no one can add people to someone else's account.
 * Every account but SupplyLogic sits under exactly one parent: a new supplier goes under SupplyLogic, a new client under the
 * chosen supplier, or under SupplyLogic when it is managed directly (no supplier chosen).
 */
export type CreateUserInput =
  | { mode: 'OWN_ACCOUNT'; person: PersonInput; roles: Role[]; isAccountAdmin?: boolean }
  | { mode: 'SUPPLY_LOGIC'; person: PersonInput; roles: Role[]; isAccountAdmin?: boolean }
  | { mode: 'EXISTING_SUPPLIER'; supplierAccountId: string; person: PersonInput; roles: Role[]; isAccountAdmin?: boolean }
  | { mode: 'NEW_SUPPLIER'; accountName: string; logoUrl: string | null; address?: Address; person: PersonInput; roles?: Role[] }
  | { mode: 'EXISTING_CLIENT'; clientAccountId: string; person: PersonInput; roles: Role[]; isAccountAdmin?: boolean }
  | { mode: 'NEW_CLIENT'; supplierAccountId?: string; accountName: string; logoUrl: string | null; address?: Address; person: PersonInput; roles?: Role[] }

export interface CreateUserResult {
  user: User
  account: Account
  createdAccount: boolean
}

/** Accounts are only ever created as SUPPLIER or CLIENT. SupplyLogic is a single account the system provides. */
const CREATABLE_ACCOUNT_TYPES: AccountType[] = ['SUPPLIER', 'CLIENT']

function validLogo(logoUrl: string | null): string | null {
  if (logoUrl === null) return null
  if (!/^data:image\/(png|jpeg|webp|gif|svg\+xml);/.test(logoUrl) && !/^(https?:\/\/|\/)/.test(logoUrl)) return 'The logo is not a supported image.'
  if (logoUrl.length > appConfig.maxLogoPreviewBytes) return 'The logo is too large. Upload a smaller image.'
  return null
}

/**
 * The accounts, the links between them and the people the signed-in user is allowed to see, loaded into the directory the rest of
 * the app reads. SupplyLogic sees everyone; a supplier or a client is sent only its own account's people (`accountId` is the
 * user's own account). `userId` is who is asking: given while signing in, otherwise the tab's session.
 */
export function loadDirectory(userId?: string): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const askingId = userId ?? getState().auth.session?.userId
    if (!askingId) throw new Error('Sign in to load the directory.')
    // Read through the query cache: the same entries the screens' hooks use, so nothing is fetched twice.
    const [accounts, relationships, me] = await Promise.all([
      queryClient.fetchQuery(accountsQueries.all()),
      queryClient.fetchQuery(accountRelationshipsQueries.all()),
      queryClient.fetchQuery(usersQueries.detail(askingId)),
    ])
    const mine = accounts.find((account) => account.id === me.accountId)
    // An account that is not SupplyLogic (or not known at all) never gets other accounts' people.
    const users = await queryClient.fetchQuery(usersQueries.all(mine?.accountType === 'SUPPLY_LOGIC' ? {} : { accountId: me.accountId }))
    dispatch(directoryLoaded(toDirectory({ accounts, users, relationships })))
  }
}

/** Reloads the directory after a change. A failed reload is not worth failing the change for: the next load catches up. */
const refresh = (): AppThunk<Promise<void>> => async (dispatch) => {
  await Promise.all([accountsKeys.root, usersKeys.root, accountRelationshipsKeys.root].map((queryKey) => queryClient.invalidateQueries({ queryKey })))
  await dispatch(loadDirectory()).catch(() => undefined)
}

/**
 * Creates a user through the backend. For a new supplier or client the account is created first (the backend creates its
 * first user with it), then that user gets the person's name and roles. Every rule is re-checked here against the loaded
 * directory, whatever the form already checked; the backend has the last word.
 */
export function createUser(input: CreateUserInput): AppThunk<Promise<Outcome<CreateUserResult>>> {
  return async (dispatch, getState) => {
    const state = getState()
    const actor = selectCurrentUser(state)
    if (!actor) return fail('Your session has ended. Sign in again.')
    if (!canManageUsers(actor)) return fail('Only admins can create users.')

    const parsed = personSchema.safeParse(input.person)
    if (!parsed.success) return fail(firstIssue(parsed) ?? 'Check the form.')
    const person = parsed.data
    const email = normalizeEmail(person.email)
    if (findUserByEmail(state, email)) return fail('A user with this email already exists.')

    const accounts = selectAllAccounts(state)
    const users = selectAllUsers(state)
    const parentTypeOf = (id: string): AccountType => selectAccountById(state, id)?.accountType ?? actor.account.accountType
    let account: Account | undefined
    let roles: Role[]
    let isAccountAdmin = false
    let created: { name: string; type: AccountType; logoUrl: string | null; address?: Address; parentId: string } | null = null

    const newAccount = (type: AccountType, accountName: string, logoUrl: string | null, address: Address | undefined, parentId: string): string | null => {
      if (!CREATABLE_ACCOUNT_TYPES.includes(type)) return 'SupplyLogic is a single system account and cannot be created.'
      if (!canCreateAccountType(actor, type)) return 'Only SupplyLogic admins can create accounts.'
      const nameError = firstIssue(accountNameSchema.safeParse(accountName))
      if (nameError) return nameError
      const logoError = validLogo(logoUrl)
      if (logoError) return logoError
      const addressError = address && firstIssue(addressSchema.safeParse(address))
      if (addressError) return addressError
      const wanted = normalizeName(accountName)
      const clash = accounts.find((item) => item.accountType === type && normalizeName(item.accountName) === wanted)
      if (clash) return `A ${type.toLowerCase()} named “${clash.accountName}” already exists. Select it to add a user.`
      if (!relationshipTypeOf(parentTypeOf(parentId), type)) return 'That account cannot be placed there.'
      created = { name: accountName.trim().replace(/\s+/g, ' '), type, logoUrl, address, parentId }
      return null
    }

    switch (input.mode) {
      case 'OWN_ACCOUNT': {
        const type = actor.account.accountType
        if (!actor.isAccountAdmin || type === 'SUPPLY_LOGIC') return fail('Only supplier and client account admins can add team members here.')
        account = actor.account
        roles = input.roles
        isAccountAdmin = Boolean(input.isAccountAdmin)
        break
      }
      case 'SUPPLY_LOGIC': {
        if (!isSupplyLogicAdmin(actor)) return fail('Only SupplyLogic admins can add SupplyLogic users.')
        const singleton = accounts.filter((item) => item.accountType === 'SUPPLY_LOGIC')
        if (singleton.length !== 1) return fail('The SupplyLogic account is missing or duplicated.')
        account = singleton[0]
        roles = input.roles
        isAccountAdmin = Boolean(input.isAccountAdmin)
        break
      }
      case 'EXISTING_SUPPLIER': {
        if (!isSupplyLogicAdmin(actor)) return fail('Only SupplyLogic admins can add users to other accounts.')
        account = selectAccountById(state, input.supplierAccountId)
        if (account?.accountType !== 'SUPPLIER') return fail('Choose an existing supplier account.')
        roles = input.roles
        isAccountAdmin = Boolean(input.isAccountAdmin)
        break
      }
      case 'EXISTING_CLIENT': {
        if (!isSupplyLogicAdmin(actor)) return fail('Only SupplyLogic admins can add users to other accounts.')
        account = selectAccountById(state, input.clientAccountId)
        if (account?.accountType !== 'CLIENT') return fail('Choose an existing client account.')
        roles = input.roles
        isAccountAdmin = Boolean(input.isAccountAdmin)
        break
      }
      case 'NEW_SUPPLIER':
      case 'NEW_CLIENT': {
        const type = input.mode === 'NEW_SUPPLIER' ? 'SUPPLIER' : 'CLIENT'
        if (!isSupplyLogicAdmin(actor)) return fail(`Only SupplyLogic admins can create ${type.toLowerCase()} accounts.`)
        // Where it sits: a supplier is always under SupplyLogic. A client is under the chosen supplier, or under SupplyLogic when none is chosen.
        let parentId = actor.accountId
        if (input.mode === 'NEW_CLIENT' && input.supplierAccountId) {
          const supplier = selectAccountById(state, input.supplierAccountId)
          if (supplier?.accountType !== 'SUPPLIER') return fail('Choose an existing supplier, or none.')
          parentId = supplier.id
        }
        const problem = newAccount(type, input.accountName, input.logoUrl, input.address, parentId)
        if (problem) return fail(problem)
        roles = input.roles?.length ? input.roles : ['DESIGNER']
        isAccountAdmin = true // the first user of an account is always its account admin
        break
      }
    }

    const target = created as { name: string; type: AccountType; logoUrl: string | null; address?: Address; parentId: string } | null
    const accountType = target?.type ?? account?.accountType
    if (!accountType) return fail('Choose an account.')
    roles = [...new Set(roles)]
    if (roles.length === 0) return fail('Choose at least one role.')
    const unknown = roles.find((role) => !ROLES_BY_ACCOUNT_TYPE[accountType].includes(role))
    if (unknown) return fail(`${unknown} is not a role for ${accountType.toLowerCase().replace('_', ' ')} accounts.`)
    if (account && !users.some((item) => item.accountId === account!.id)) isAccountAdmin = true

    try {
      let createdUser: User
      let finalAccount: Account
      if (target) {
        const address = hasAddress(target.address) ? target.address : undefined
        const made = await accountsService.create({
          accountName: target.name,
          accountType: target.type,
          email,
          phoneNumber: person.phoneNumber,
          ...(target.logoUrl && { logoUrl: target.logoUrl }),
          ...(address && { addressLine1: address.line1, city: address.city, state: address.region, postalCode: address.postalCode, country: address.country }),
          createdBy: actor.id,
        })
        finalAccount = toAccount(made) ?? { id: made.id, accountName: target.name, accountType: target.type, logoUrl: target.logoUrl, createdAt: made.createdAt }
        try {
          // Place it under its parent, then give the first user (which the backend created with the account) the person's name and roles.
          await accountRelationshipsService.create({ parentAccountId: target.parentId, childAccountId: made.id, createdBy: actor.id })
          const body = { accountId: made.id, firstName: person.firstName, lastName: person.lastName, email, phoneNumber: person.phoneNumber, roles: rolesFor(roles, true) }
          createdUser = toUser(made.user ? await usersService.update(made.user.id, { ...body, isActive: true }) : await usersService.create({ ...body, createdBy: actor.id }))
        } catch (error) {
          // Never leave an account that is not linked or has no usable first user: take it back out.
          const undone = await accountsService.remove(made.id).then(() => true, () => false)
          await dispatch(refresh())
          return fail(
            undone
              ? `“${target.name}” was not created: ${toApiError(error).message}`
              : `“${target.name}” was created but could not be finished (${toApiError(error).message}), and it could not be removed again. It has to be removed on the server before you try again.`,
          )
        }
      } else {
        finalAccount = account!
        createdUser = toUser(
          await usersService.create({ accountId: finalAccount.id, firstName: person.firstName, lastName: person.lastName, email, phoneNumber: person.phoneNumber, roles: rolesFor(roles, isAccountAdmin), createdBy: actor.id }),
        )
      }
      await dispatch(refresh())
      return ok({ user: createdUser, account: finalAccount, createdAccount: Boolean(target) })
    } catch (error) {
      return fail(toApiError(error).message)
    }
  }
}

export interface UpdateUserInput {
  userId: string
  firstName: string
  lastName: string
  phoneNumber: string
  roles: Role[]
  /** Ignored when editing yourself: nobody can take their own admin rights away. */
  isAccountAdmin: boolean
}

/** Whether the signed-in user may change or remove this person: SupplyLogic admins anyone, other admins their own account. */
function canEditUser(actor: NonNullable<ReturnType<typeof selectCurrentUser>>, target: User): boolean {
  const scope = usersScopeOf(actor)
  return canManageUsers(actor) && (scope === 'ALL' || target.accountId === actor.accountId)
}

/**
 * Edits a person's name, phone, roles and admin rights. The email and the account are kept as they are. Re-checked against
 * the loaded directory; the backend has the last word.
 */
export function updateUser(input: UpdateUserInput): AppThunk<Promise<Outcome<User>>> {
  return async (dispatch, getState) => {
    const state = getState()
    const actor = selectCurrentUser(state)
    if (!actor) return fail('Your session has ended. Sign in again.')
    const target = selectUserById(state, input.userId)
    if (!target) return fail('This user no longer exists.')
    if (!canEditUser(actor, target)) return fail('You can only edit people in your own account.')

    const parsed = personSchema.safeParse({ firstName: input.firstName, lastName: input.lastName, email: target.email, phoneNumber: input.phoneNumber })
    if (!parsed.success) return fail(firstIssue(parsed) ?? 'Check the form.')
    const account = selectAccountById(state, target.accountId)
    const roles = [...new Set(input.roles)]
    if (roles.length === 0) return fail('Choose at least one role.')
    if (!account || roles.some((role) => !ROLES_BY_ACCOUNT_TYPE[account.accountType].includes(role))) return fail('That role is not available for this account.')
    const admin = target.id === actor.id ? target.isAccountAdmin : input.isAccountAdmin

    try {
      const saved = await usersService.update(target.id, {
        accountId: target.accountId,
        firstName: parsed.data.firstName,
        lastName: parsed.data.lastName,
        email: target.email,
        phoneNumber: parsed.data.phoneNumber,
        roles: rolesFor(roles, admin),
        isActive: target.isActive ?? true,
      })
      await dispatch(refresh())
      return ok(toUser(saved))
    } catch (error) {
      return fail(toApiError(error).message)
    }
  }
}

/** Removes a person. Nobody can remove themselves, so an account is never left with a signed-in user who no longer exists. */
export function deleteUser(userId: string): AppThunk<Promise<Outcome<User>>> {
  return async (dispatch, getState) => {
    const state = getState()
    const actor = selectCurrentUser(state)
    if (!actor) return fail('Your session has ended. Sign in again.')
    const target = selectUserById(state, userId)
    if (!target) return fail('This user no longer exists.')
    if (target.id === actor.id) return fail('You cannot remove yourself.')
    if (!canEditUser(actor, target)) return fail('You can only remove people in your own account.')
    try {
      await usersService.remove(target.id)
      await dispatch(refresh())
      return ok(target)
    } catch (error) {
      return fail(toApiError(error).message)
    }
  }
}
